import { useEffect, useId } from 'react'

/** Bloquea el scroll del fondo mientras hay un overlay abierto.
 *
 *  Este archivo ya se reescribió dos veces por el mismo síntoma: la
 *  página deja de scrollear —"se queda pegada"— y no hay forma de
 *  recuperarla sin recargar. Primero cada overlay guardaba y restauraba
 *  el overflow anterior por su cuenta, y se pisaban entre sí. Después
 *  vino un contador, que arregló ese caso pero trajo el suyo: basta un
 *  bloqueo que nunca recibe su liberación —un efecto que se remonta, un
 *  error entre medio, una recarga en caliente que reinicia el módulo—
 *  para que el contador quede en 1 para siempre.
 *
 *  La raíz de las dos versiones anteriores es la misma: el estado del
 *  scroll vivía en un número que había que mantener a mano. Acá se
 *  guarda QUIÉN bloquea, no CUÁNTOS. Con ids, bloquear dos veces con el
 *  mismo id, liberar dos veces, o liberar algo que nunca bloqueó son
 *  operaciones inofensivas: el estado siempre se puede reconstruir
 *  mirando el conjunto, nunca se "desfasa".
 *
 *  Se bloquean <html> y <body> a la vez a propósito. `html` lleva
 *  `overflow-x: hidden` en index.css, y eso corta la propagación del
 *  overflow de `body` hacia el viewport: con `body` solo, el scroll del
 *  fondo seguía vivo en escritorio, que es donde no hay rebote táctil
 *  que lo disimule. */
const bloqueos = new Set<string>()

function sincronizar() {
  document.documentElement.classList.toggle('scroll-bloqueado', bloqueos.size > 0)
}

export function bloquearScroll(id: string) {
  bloqueos.add(id)
  sincronizar()
}

export function liberarScroll(id: string) {
  bloqueos.delete(id)
  sincronizar()
}

/** Red de seguridad. Al cambiar de ruta no queda ningún overlay en
 *  pantalla —los monta la página que se está dejando atrás—, así que si
 *  algo quedó registrado es que se escapó. Con esto, aunque aparezca un
 *  caso que no previmos, el scroll se recupera navegando a cualquier
 *  lado en vez de obligar a recargar la app. */
export function liberarTodoElScroll() {
  bloqueos.clear()
  sincronizar()
}

/** La única forma de bloquear el scroll desde un componente. Es un hook
 *  y no un par de llamadas sueltas para que no exista la posibilidad de
 *  escribir el bloqueo y olvidar la liberación: React se encarga del
 *  cierre al desmontar, y el id sale de `useId`, distinto por instancia
 *  —dos hojas abiertas a la vez no se pisan. */
export function useBloqueoScroll(activo: boolean) {
  const id = useId()
  useEffect(() => {
    if (!activo) return
    bloquearScroll(id)
    return () => liberarScroll(id)
  }, [activo, id])
}
